import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContractDocSource,
  ContractStatus,
  IContractDoc,
  getContractDocDisplayStatus,
  isPlacementContractPdfByStatus,
  opensInCandidateContractHtmlEditor,
} from 'store/redux-store/placement-candidate-contract-doc-editor/types';
import { CardDate, DocumentCard, useDocCardStyles } from '../customComponents/DocumentCard';
import { customSwitch } from 'utils/customSwitchCase/customSwitch';
import { TFunction } from 'i18next';

export const ContractDocCard: React.FC<{
    card: IContractDoc;
    isSelected: boolean;
    interactionLocked?: boolean;
    [key: string]: any;
  }> = ({ card, isSelected, interactionLocked = false, ...props }) => {
    const { t } = useTranslation();
    const { isCreatedFromClassic, isCardClickable, publishedToPassport, displayStatus } = React.useMemo(
      () => ({
        isCreatedFromClassic: card.source === ContractDocSource.amieClassic,
        isCardClickable:
          !interactionLocked &&
          (opensInCandidateContractHtmlEditor(card) || isPlacementContractPdfByStatus(card)),
        publishedToPassport: [ContractStatus.published, ContractStatus.signed].includes(card.status),
        displayStatus: getContractDocDisplayStatus(card),
      }),
      [card, interactionLocked],
    );
  
    const { classes, cx } = useDocCardStyles({ isCardClickable });
  
    return (
      <DocumentCard
        {...props}
        {...{ classes, cx, t }}
        card={card}
        isSelected={isSelected}
        isCreatedFromClassic={isCreatedFromClassic}
        isCardClickable={isCardClickable}
        publishedToPassport={publishedToPassport}
        disabledStatusTooltip={undefined}
        displayStatus={displayStatus}
        datesComponent={getContractCardDates({ card, classes, t })(displayStatus)}
      />
    );
  };

  const getContractCardDates = ({ card, classes, t }: { card: IContractDoc; classes; t: TFunction }) => {
    const statusChangedByName = card.lastUpdatedByName ?? card.updatedByName;
    return customSwitch({
      [ContractStatus.draft]: (
        <CardDate
          title={t('templateEditor.templateCard.generatedBy')}
          name={card.draftCreatedByName}
          date={card.view.draftGeneratedDate}
          classes={classes}
        />
      ),
      [ContractStatus.created]: (
        <>
          <CardDate
            title={t('templateEditor.templateCard.createdBy')}
            name={card.docCreatedByName}
            date={card.view.docCreatedDate}
            classes={classes}
          />
          <CardDate
            title={t('templateEditor.templateCard.generatedBy')}
            name={card.draftCreatedByName}
            date={card.view.draftGeneratedDate}
            classes={classes}
          />
        </>
      ),
      [ContractStatus.published]: (
        <CardDate
          title={t('templateEditor.templateCard.statusChangedBy')}
          name={statusChangedByName}
          date={card.view.updateDate}
          classes={classes}
        />
      ),
      [ContractStatus.publishedVoid]: (
        <>
          <CardDate
            title={t('templateEditor.templateCard.publishedBy')}
            name={card.publishedByName}
            date={card.view.publishedDate}
            classes={classes}
          />
          <CardDate
            title={t('templateEditor.templateCard.voidedBy')}
            name={card.voidedByName}
            date={card.view.voidedDate}
            classes={classes}
          />
        </>
      ),
      [ContractStatus.signed]: (
        <>
          <CardDate
            title={t('templateEditor.templateCard.statusChangedBy')}
            name={statusChangedByName}
            date={card.view.updateDate}
            classes={classes}
          />
          <CardDate title={t('templateEditor.templateCard.signed')} date={card.view.signedDate} classes={classes} />
        </>
      ),
    })(
      <CardDate
        title={t('templateEditor.templateCard.statusChangedBy')}
        name={statusChangedByName}
        date={card.view.updateDate}
        classes={classes}
      />,
    );
  };
    